import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import Seo from "@/components/Seo";
import AuthShell from "@/components/AuthShell";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Reset password" description="Reset your StUpEco Workspace password." path="/forgot-password" />
      <AuthShell
        title="Forgot password?"
        subtitle="Enter your email and we'll send you a reset link."
        footer={<Link to="/login" className="font-medium text-foreground hover:underline" data-testid="back-to-login-link">Back to login</Link>}
      >
        {sent ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-card p-8 text-center" data-testid="forgot-success">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal/12 text-brand-teal">
              <MailCheck className="h-6 w-6" />
            </span>
            <div>
              <h3 className="font-display text-lg font-semibold">Check your inbox</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                If an account exists for <span className="font-medium text-foreground">{email}</span>, a reset link is on its way. It expires in 1 hour.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-email-input" placeholder="you@company.com" />
            </div>
            <button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="w-full rounded-full bg-foreground px-6 py-3.5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60">
              {loading ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}
      </AuthShell>
    </>
  );
}
